import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ConfigProvider } from 'antd';
import zhCN from 'antd/locale/zh_CN';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import 'dayjs/locale/zh-cn';
import { AuthProvider } from './contexts/AuthContext';
import PermissionGuard from './components/PermissionGuard';
import AppLayout from './components/Layout/AppLayout';
import Dashboard from './pages/Dashboard/Dashboard';
import WarningsAnalysis from './pages/Warnings/WarningsAnalysis';
import CitiesManagement from './pages/Management/CitiesManagement';
import TrainingBusinesses from './pages/Management/TrainingBusinesses';
import TrainingElements from './pages/Management/TrainingElements';
import TrainingStandards from './pages/Management/TrainingStandards';
import UserManagement from './pages/Management/UserManagement';
import ImportData from './components/ImportData';
import InspectionReportView from './components/InspectionReportView';
import Demo from './components/Demo';
import { Login, Register } from './pages/Auth';
import TrainingInfoCollection from './pages/TrainingInfo/TrainingInfoCollection';
import TrainingDataAnalysis from './pages/TrainingInfo/TrainingDataAnalysis';
import FacultyList from './pages/FacultyList';
import FacultyForm from './pages/FacultyForm';
import FacultyDetail from './pages/FacultyDetail';
import FacultyAnalytics from './pages/FacultyAnalytics';
import { isAuthenticated } from './services/api';
import 'antd/dist/reset.css';
import './App.css';

// 受保护的路由组件
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const authenticated = isAuthenticated();
  return authenticated ? <>{children}</> : <Navigate to="/login" replace />;
};

// 认证路由组件（已登录用户重定向到仪表盘）
const AuthRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const authenticated = isAuthenticated();
  return !authenticated ? <>{children}</> : <Navigate to="/dashboard" replace />;
};

// 设置 dayjs 全局配置
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.locale('zh-cn');
// 设置默认时区为北京时间
dayjs.tz.setDefault('Asia/Shanghai');

function App() {
  return (
    <ConfigProvider locale={zhCN}>
      <AuthProvider>
        <Router>
          <div className="App">
            <Routes>
              {/* 认证页面 - 未登录用户可访问 */}
              <Route path="/login" element={<AuthRoute><Login /></AuthRoute>} />
              <Route path="/register" element={<AuthRoute><Register /></AuthRoute>} />

              {/* 受保护的页面 - 需要登录 */}
              <Route path="/dashboard" element={
                <ProtectedRoute>
                  <AppLayout>
                    <Dashboard />
                  </AppLayout>
                </ProtectedRoute>
              } />

              <Route path="/demo" element={
                <ProtectedRoute>
                  <AppLayout>
                    <Demo />
                  </AppLayout>
                </ProtectedRoute>
              } />

              <Route path="/import-new" element={
                <ProtectedRoute>
                  <AppLayout>
                    <ImportData />
                  </AppLayout>
                </ProtectedRoute>
              } />

              <Route path="/reports-new" element={
                <ProtectedRoute>
                  <AppLayout>
                    <InspectionReportView />
                  </AppLayout>
                </ProtectedRoute>
              } />

              <Route path="/warnings" element={
                <ProtectedRoute>
                  <AppLayout>
                    <WarningsAnalysis />
                  </AppLayout>
                </ProtectedRoute>
              } />

              {/* 培训信息收集管理 - 登录用户可访问，权限控制在组件内部实现 */}
              <Route path="/training-info" element={
                <ProtectedRoute>
                  <AppLayout>
                    <TrainingInfoCollection />
                  </AppLayout>
                </ProtectedRoute>
              } />

              {/* 培训数据分析 - 登录用户可访问 */}
              <Route path="/training-analysis" element={
                <ProtectedRoute>
                  <AppLayout>
                    <TrainingDataAnalysis />
                  </AppLayout>
                </ProtectedRoute>
              } />

              {/* 师资管理 - 登录用户可访问 */}
              <Route path="/faculty" element={
                <ProtectedRoute>
                  <AppLayout>
                    <FacultyList />
                  </AppLayout>
                </ProtectedRoute>
              } />

              <Route path="/faculty/add" element={
                <ProtectedRoute>
                  <AppLayout>
                    <FacultyForm />
                  </AppLayout>
                </ProtectedRoute>
              } />

              <Route path="/faculty/edit/:id" element={
                <ProtectedRoute>
                  <AppLayout>
                    <FacultyForm mode="edit" />
                  </AppLayout>
                </ProtectedRoute>
              } />

              <Route path="/faculty/detail/:id" element={
                <ProtectedRoute>
                  <AppLayout>
                    <FacultyDetail />
                  </AppLayout>
                </ProtectedRoute>
              } />

              <Route path="/faculty/analytics" element={
                <ProtectedRoute>
                  <AppLayout>
                    <FacultyAnalytics />
                  </AppLayout>
                </ProtectedRoute>
              } />

              {/* 管理员页面 - 需要管理员权限 */}
              <Route path="/management/cities" element={
                <ProtectedRoute>
                  <AppLayout>
                    <PermissionGuard requiredRole="admin">
                      <CitiesManagement />
                    </PermissionGuard>
                  </AppLayout>
                </ProtectedRoute>
              } />

              <Route path="/management/businesses" element={
                <ProtectedRoute>
                  <AppLayout>
                    <PermissionGuard requiredRole="admin">
                      <TrainingBusinesses />
                    </PermissionGuard>
                  </AppLayout>
                </ProtectedRoute>
              } />

              <Route path="/management/elements" element={
                <ProtectedRoute>
                  <AppLayout>
                    <PermissionGuard requiredRole="admin">
                      <TrainingElements />
                    </PermissionGuard>
                  </AppLayout>
                </ProtectedRoute>
              } />

              <Route path="/management/standards" element={
                <ProtectedRoute>
                  <AppLayout>
                    <PermissionGuard requiredRole="admin">
                      <TrainingStandards />
                    </PermissionGuard>
                  </AppLayout>
                </ProtectedRoute>
              } />

              <Route path="/management/users" element={
                <ProtectedRoute>
                  <AppLayout>
                    <PermissionGuard requiredRole="admin">
                      <UserManagement />
                    </PermissionGuard>
                  </AppLayout>
                </ProtectedRoute>
              } />

              {/* 根路径重定向 */}
              <Route path="/" element={<Navigate to="/dashboard" replace />} />
            </Routes>
          </div>
        </Router>
      </AuthProvider>
    </ConfigProvider>
  );
}

export default App;
